import { FC } from 'react';
import classNames from 'classnames';
import { InputSize } from 'components/common/types/Input.types';
import Search from 'lib/Search';
import BaseSelect from 'lib/BaseSelect';
import { Label } from 'lib/Label';
import { POIList } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/POIList';
import {
  CustomPOIUpload,
  CustomPOICategoryList,
} from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/CustomPOIUpload';
import { PoiSelectionContentProps } from './ProductPoi.types';

export const PoiSelectionContent: FC<PoiSelectionContentProps> = ({
  isMainLevel,
  navigationStack,
  selectedProvider,
  dataProviders,
  searchTerm,
  currentPoiOptions,
  currentFocusedItemIndex,
  getSelectedPOIIncludeAndRadius,
  getSelectedPOIIncludedExcludedChildrenCounter,
  isItemSelected,
  onChangeRadius,
  onIncludeExcludeChange,
  resetCurrentFocusedItemIndex,
  navigateForward,
  onTransitionComplete,
  transition,
  handleProviderSelect,
  handleSearchChange,
  customPoiCategories,
  onFileChange,
  onDeleteCustomCategory,
  hasReachedFileUploadLimit,
  isInsideCustomCategory,
}) => {
  const showNoResults = searchTerm.length > 0 && currentPoiOptions.length === 0 && customPoiCategories.length === 0;
  const showPoiList = selectedProvider && !showNoResults;
  const options = dataProviders.map((provider) => ({ code: provider, name: provider }));

  return (
    <div className="flex flex-col flex-1 h-full">
      <div className="mb-2">
        <Search size={InputSize.MEDIUM} value={searchTerm} placeholder="Search POI" onChange={handleSearchChange} />
      </div>
      <div
        className={classNames('transition-all delay-300 duration-300 ease-in-out overflow-hidden', {
          'opacity-100 max-h-[500px]': isMainLevel,
          'opacity-0 max-h-0': !isMainLevel,
        })}
      >
        <CustomPOIUpload onFileChange={onFileChange} hasReachedFileUploadLimit={hasReachedFileUploadLimit} />
        <CustomPOICategoryList
          customPoiCategories={customPoiCategories}
          isItemSelected={isItemSelected}
          getSelectedPOIIncludeAndRadius={getSelectedPOIIncludeAndRadius}
          getSelectedPOIIncludedExcludedChildrenCounter={getSelectedPOIIncludedExcludedChildrenCounter}
          navigateForward={navigateForward}
          onChangeRadius={onChangeRadius}
          onIncludeExcludeChange={onIncludeExcludeChange}
          resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
          onDeleteCustomCategory={onDeleteCustomCategory}
        />
      </div>
      <div
        className={classNames('transition-all delay-300 duration-300 ease-in-out', {
          'opacity-100 my-0 mb-2': isMainLevel,
          'opacity-0 invisible -my-[63px] mb-0': navigationStack.length > 0,
        })}
      >
        <Label label="Data provider" />
        <BaseSelect
          dataTestId="poi-data-providers"
          name="data providers"
          options={options}
          selectedValue={{ code: selectedProvider, name: selectedProvider }}
          onSelect={handleProviderSelect}
        />
      </div>
      {showNoResults && <div>There are no results matching your search</div>}
      {showPoiList && (
        <POIList
          currentFocusedItemIndex={currentFocusedItemIndex}
          getSelectedPOIIncludeAndRadius={getSelectedPOIIncludeAndRadius}
          getSelectedPOIIncludedExcludedChildrenCounter={getSelectedPOIIncludedExcludedChildrenCounter}
          isItemSelected={isItemSelected}
          onChangeRadius={onChangeRadius}
          onIncludeExcludeChange={onIncludeExcludeChange}
          resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
          poiOptions={currentPoiOptions}
          navigateForward={navigateForward}
          onTransitionComplete={onTransitionComplete}
          isMainLevel={isMainLevel}
          transition={transition}
          selectedProvider={selectedProvider}
          hideParentIncludeExclude={false}
          isDeselectAllowed={!isInsideCustomCategory}
        />
      )}
    </div>
  );
};
